import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Phone } from 'lucide-react';
import { fastapiService } from '../../services/fastapiService';
import { useAdminBasePath } from '../../hooks/useAdminBasePath';

type SenderSettings = {
  indian_default_e164?: string;
  international_default_e164?: string;
  voice_enabled?: boolean;
  sms_enabled?: boolean;
  notification_voice_credits?: number;
};

export const NotificationSendersPage: React.FC = () => {
  const base = useAdminBasePath();
  const isEnterprise = true;

  const [form, setForm] = useState<SenderSettings>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = (await fastapiService.getNotificationSenders()) as { data?: SenderSettings };
      setForm(res?.data || {});
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      await fastapiService.updateNotificationSenders(form);
      setMessage('Notification sender settings saved.');
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const titleClass = isEnterprise ? 'text-2xl font-bold text-enterprise-900' : 'text-2xl font-bold text-admin-900';
  const labelClass = isEnterprise ? 'text-sm font-medium text-enterprise-800' : 'text-sm font-medium text-admin-800';
  const inputClass = isEnterprise
    ? 'mt-1 w-full rounded-lg border border-enterprise-200 px-3 py-2 text-sm'
    : 'mt-1 w-full rounded-lg border border-admin-200 px-3 py-2 text-sm';

  return (
    <div className="max-w-2xl">
      <Link
        to={`${base}/settings`}
        className="mb-6 inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to settings
      </Link>

      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
          <Phone className="h-5 w-5" />
        </div>
        <div>
          <h1 className={titleClass}>Notification sender numbers</h1>
          <p className="mt-1 text-sm text-gray-600">
            Platform defaults for user notification SMS (international) and voice calls (all regions).
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
      )}
      {message && (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          {message}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <form onSubmit={handleSave} className="space-y-5 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div>
            <label className={labelClass}>Indian default (E.164)</label>
            <input
              className={inputClass}
              value={form.indian_default_e164 || ''}
              onChange={(e) => setForm((f) => ({ ...f, indian_default_e164: e.target.value }))}
              placeholder="+911724889083"
            />
            <p className="mt-1 text-xs text-gray-500">Used for voice notifications when workspace country is India.</p>
          </div>
          <div>
            <label className={labelClass}>International default (E.164)</label>
            <input
              className={inputClass}
              value={form.international_default_e164 || ''}
              onChange={(e) => setForm((f) => ({ ...f, international_default_e164: e.target.value }))}
              placeholder="+15197073340"
            />
            <p className="mt-1 text-xs text-gray-500">Used for SMS and voice when workspace country is not India.</p>
          </div>
          <div className="flex flex-wrap gap-6">
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.voice_enabled !== false}
                onChange={(e) => setForm((f) => ({ ...f, voice_enabled: e.target.checked }))}
              />
              Voice notifications enabled
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.sms_enabled !== false}
                onChange={(e) => setForm((f) => ({ ...f, sms_enabled: e.target.checked }))}
              />
              SMS notifications enabled (international)
            </label>
          </div>
          <div>
            <label className={labelClass}>Voice notification credits (reference)</label>
            <input
              type="number"
              min={0}
              step={0.1}
              className={inputClass}
              value={form.notification_voice_credits ?? 2}
              onChange={(e) =>
                setForm((f) => ({ ...f, notification_voice_credits: parseFloat(e.target.value) || 0 }))
              }
            />
            <p className="mt-1 text-xs text-gray-500">
              Billing uses workspace tier voice rate with action type notification_voice.
            </p>
          </div>
          <button
            type="submit"
            disabled={saving}
            className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save settings'}
          </button>
        </form>
      )}
    </div>
  );
};
